import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Mail, Menu, X } from 'lucide-react'
import { supabaseClient } from '../config/supabase'
import toast from 'react-hot-toast'

const EmployerForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }

    setIsLoading(true)

    const redirectTo = `${window.location.origin}/reset-password`

    const { error: resetError } = await supabaseClient.auth.resetPasswordForEmail(email.trim(), {
      redirectTo,
    })

    setIsLoading(false)

    if (resetError) {
      setError(resetError.message)
      return
    }

    setSent(true)
    toast.success('Password reset email sent!')
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        backgroundImage: 'url(/uc_nobg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <Link to="/" className="flex items-center">
              <img src="/trailblaze_logo.png" alt="Trailblaze" className="h-20" />
            </Link>

            <nav className="hidden md:flex space-x-8">
              <Link to="/news" className="text-gray-600 hover:text-gray-900">News &amp; Updates</Link>
              <Link to="/#features" className="text-gray-600 hover:text-gray-900">Features</Link>
              <Link to="/#benefits" className="text-gray-600 hover:text-gray-900">Benefits</Link>
              <Link to="/#about" className="text-gray-600 hover:text-gray-900">About UCLM</Link>
            </nav>

            <div className="hidden md:flex">
              <Link
                to="/login/employer"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Employer Login
              </Link>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-600" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600" />
              )}
            </button>
          </div>

          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-4">
                <Link to="/news" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>News &amp; Updates</Link>
                <Link to="/#features" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>Features</Link>
                <Link to="/#benefits" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>Benefits</Link>
                <Link to="/#about" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>About UCLM</Link>
                <div className="pt-4 border-t border-gray-200">
                  <Link
                    to="/login/employer"
                    className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Employer Login
                  </Link>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-8">
            <div className="text-center mb-8">
              <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-2">Trailblaze</div>
              <p className="text-sm sm:text-base text-gray-600">Employer Portal</p>
            </div>

            {sent ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-800">Check your email</h2>
                <p className="text-sm text-gray-600">
                  We sent a password reset link to{' '}
                  <span className="font-medium text-gray-900">{email}</span>.
                  Click the link in the email to set a new password.
                </p>
                <p className="text-xs text-gray-500">
                  Didn't receive it? Check your spam folder or{' '}
                  <button
                    onClick={() => setSent(false)}
                    className="text-blue-600 hover:underline"
                  >
                    try again
                  </button>
                  .
                </p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-1">Forgot your password?</h2>
                  <p className="text-sm text-gray-500">
                    Enter the email address linked to your employer account and we'll send you a reset link.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value)
                        setError('')
                      }}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Enter your employer email"
                      autoFocus
                    />
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-xs sm:text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white font-semibold py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <Link to="/login/employer" className="text-xs sm:text-sm text-blue-600 hover:underline">
                    Back to employer login
                  </Link>
                </div>
              </>
            )}
          </div>

          <div className="mt-6 flex justify-center">
            <Link
              to="/"
              className="flex items-center gap-2 text-xs sm:text-sm text-blue-600 hover:text-blue-800 transition-colors"
            >
              <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmployerForgotPasswordPage
